import { IStargateClient } from '@atlassiansox/asc-stargate';
import { useStargateClient } from '@atlassiansox/bxpkit-core';
import debounce from 'lodash.debounce';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { AnalyticsTrackType } from '../model';

export type SiteNameValidation = {
  readonly isValidating: boolean;
  readonly isValid: boolean;
  readonly message?: string;
};

export function useSiteNameValidation(
  siteName: string,
  analyticsTrack: AnalyticsTrackType,
  isDeferredSiteNameEnabled?: boolean,
  activateProductError?: boolean
): SiteNameValidation {
  const [{ isValidating, isValid, message }, setValidation] =
    useState<SiteNameValidation>({
      isValidating: false,
      isValid: false
    });
  const stargateClient = useStargateClient();
  const re = /^[a-z0-9]+[a-z0-9-]+[a-z0-9]+$/;

  useEffect(() => {
    validateSiteNameDebounced.cancel();
    if (siteName.length === 0) {
      if (isDeferredSiteNameEnabled) {
        return setValidation({
          isValidating: false,
          isValid: true
        });
      } else if (isValid !== false) {
        return setValidation({
          isValidating: false,
          isValid: false,
          message: 'Site name is required'
        });
      } else {
        return;
      }
    } else if (siteName.length < 3) {
      return setValidation({
        isValidating: false,
        isValid: false,
        message:
          'Your site name must be at least 3 characters long. Use only lowercase letters and numbers.'
      });
    } else if (siteName.length > 50) {
      return setValidation({
        isValidating: false,
        isValid: false,
        message: "That's a bit too long — try something 50 characters or less."
      });
    } else if (!siteName.match(re)) {
      return setValidation({
        isValidating: false,
        isValid: false,
        message:
          'Your site name must be at least 3 characters long. Use only lowercase letters and numbers.'
      });
    } else if (activateProductError) {
      return setValidation({
        isValidating: false,
        isValid: false,
        message: 'This site name is already taken. Please try another name.'
      });
    } else {
      setValidation({ isValidating: true, isValid: false });
      void validateSiteNameDebounced(
        siteName,
        setValidation,
        analyticsTrack,
        stargateClient
      );
    }
  }, [siteName, activateProductError]);

  return { isValidating, isValid, message };
}

const validateSiteNameDebounced = debounce(validateSiteName, 1000, {
  trailing: true
});

async function validateSiteName(
  siteName: string,
  setValidation: Dispatch<SetStateAction<SiteNameValidation>>,
  analyticsTrack: AnalyticsTrackType,
  stargateClient: IStargateClient
) {
  const { newRelic } = analyticsTrack;
  try {
    const { availability } = await stargateClient.checkCloudNameAvailability({
      cloudName: siteName,
      webPlatform: 'magnolia'
    });
    newRelic.trackAjax(
      'react-signup-site-validation',
      { status: '200', apiUrl: 'site-availability-url' },
      true
    );
    switch (availability) {
      case 'AVAILABLE':
        return setValidation({ isValidating: false, isValid: true });
      default:
        return setValidation({
          isValidating: false,
          isValid: false,
          message: 'Site name is unavailable'
        });
    }
  } catch (error) {
    newRelic.trackAjax('react-signup-site-validation', error, false);
    return setValidation({
      isValidating: false,
      isValid: false,
      message: 'Network error'
    });
  }
}
